import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import ConfirmModal from '../../components/ConfirmModal.jsx';

const EMPTY = {
  gtMin: '',
  gtMax: '',
  minTugCount: '',
  minBollardPullTons: '',
  compulsoryFloor: false,
  exemptionRules: '',
};

export default function AllocationRulesPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Set a form value and clear that field's error (plus any dependent ones).
  const setField = (key, value, ...alsoClear) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of [key, ...alsoClear, 'form']) delete next[k];
      return next;
    });
  };

  // Booking compliance checker.
  const [check, setCheck] = useState({ gt: '', tugCount: '' });
  const [checkResult, setCheckResult] = useState(null);
  const [checkErrors, setCheckErrors] = useState({});

  // Port Safety Compulsory Floors (advisory, front-end-only in this build).
  const [selectedPort, setSelectedPort] = useState('');
  const [enforceHardFloor, setEnforceHardFloor] = useState(false);
  const [thrusterRules, setThrusterRules] = useState('');

  // Derive available ports from already-loaded allocation rules.
  const portOptions = Array.from(
    new Set((rules || []).map((r) => r.port_name).filter(Boolean)),
  );

  const load = useCallback(async () => {
    const res = await api.listAllocationRules();
    setRules(res || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load allocation rules.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const openAdd = () => {
    setErrors({});
    setForm(EMPTY);
    setModalOpen(true);
  };

  const validate = () => {
    const next = {};
    if (form.gtMin === '') next.gtMin = 'GT minimum is required';
    else if (Number(form.gtMin) < 0) next.gtMin = 'GT minimum cannot be negative';
    if (form.gtMax !== '' && Number(form.gtMax) < Number(form.gtMin))
      next.gtMax = 'GT maximum must be ≥ GT minimum';
    if (!form.minTugCount || Number(form.minTugCount) < 1) next.minTugCount = 'At least 1 tug';
    if (form.minBollardPullTons === '' || !(Number(form.minBollardPullTons) > 0))
      next.minBollardPullTons = 'Minimum bollard pull is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setPageMsg(null);
    try {
      await api.createAllocationRule({
        gtMin: Number(form.gtMin),
        gtMax: form.gtMax === '' ? null : Number(form.gtMax),
        minTugCount: Number(form.minTugCount),
        minBollardPullTons: Number(form.minBollardPullTons),
        compulsoryFloor: form.compulsoryFloor,
        exemptionRules: form.exemptionRules.trim() || null,
      });
      setPageMsg({ type: 'success', text: 'Allocation rules configuration saved.' });
      await load();
      setModalOpen(false);
    } catch (err) {
      setErrors((p) => ({ ...p, form: err.message || 'Failed to save rule.' }));
    } finally {
      setSaving(false);
    }
  };

  const removeRule = async (rule) => {
    setPageMsg(null);
    setDeleting(true);
    try {
      await api.deleteAllocationRule(rule.id);
      setPageMsg({ type: 'success', text: 'Allocation rule deleted.' });
      await load();
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to delete rule.' });
    } finally {
      setDeleting(false);
      setConfirmDel(null);
    }
  };

  const runCheck = async (e) => {
    e.preventDefault();
    setCheckResult(null);
    const next = {};
    if (check.gt === '') next.gt = 'Vessel GT is required';
    else if (Number(check.gt) < 0) next.gt = 'Vessel GT cannot be negative';
    if (check.tugCount === '') next.tugCount = 'Tug count is required';
    else if (Number(check.tugCount) < 0) next.tugCount = 'Tug count cannot be negative';
    else if (!Number.isInteger(Number(check.tugCount))) next.tugCount = 'Tug count must be a whole number';
    setCheckErrors(next);
    if (Object.keys(next).length) return;
    try {
      const res = await api.checkAllocation({ gt: Number(check.gt), tugCount: Number(check.tugCount) });
      setCheckResult({ ...res, gt: Number(check.gt) });
    } catch (err) {
      setCheckResult({ error: err.message || 'Check failed' });
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="allocation-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-balance-scale me-2" aria-hidden="true" />
                Allocation Rules &amp; Compulsory Floors
              </h3>
              <p className="text-muted mb-0 small">
                Map vessel GT bands to minimum tug counts and PPA compulsory safety floors.
              </p>
            </div>
            <span className="badge bg-primary p-2">PPA Safety</span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="allocation-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="allocation-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: allocation matrix */}
          <div className="col-lg-8 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-table me-2" aria-hidden="true" />
                  Tug Allocation Matrix
                </h5>
                <button className="btn btn-sm btn-primary" onClick={openAdd} data-testid="add-rule">
                  <i className="fas fa-plus me-1" aria-hidden="true" />
                  Add Allocation Rule
                </button>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>GT Min</th>
                        <th>GT Max</th>
                        <th>Min Tugs</th>
                        <th>Min Bollard Pull</th>
                        <th>Port</th>
                        <th>Compulsory Floor</th>
                        <th aria-label="actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {rules.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            No allocation rules yet.
                          </td>
                        </tr>
                      )}
                      {rules.map((r) => (
                        <tr key={r.id} data-testid="rule-row" data-gtmin={r.gt_min} data-gtmax={r.gt_max ?? ''}>
                          <td className="fw-bold">{r.gt_min}</td>
                          <td>{r.gt_max ?? '∞'}</td>
                          <td data-testid="rule-mintugs">{r.min_tug_count}</td>
                          <td>{r.min_bollard_pull_tons != null ? `${Number(r.min_bollard_pull_tons)} T` : '—'}</td>
                          <td className="small">{r.port_name || 'All ports'}</td>
                          <td>
                            {r.compulsory_floor ? (
                              <span className="badge bg-danger" data-testid="rule-compulsory">
                                <i className="fas fa-shield-alt me-1" aria-hidden="true" />
                                Active
                              </span>
                            ) : (
                              <span className="badge bg-light text-dark border">Off</span>
                            )}
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setConfirmDel(r)}
                              aria-label="Delete rule"
                              data-testid="rule-delete"
                            >
                              <i className="fas fa-trash" aria-hidden="true" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: port safety floors + booking compliance checker */}
          <div className="col-lg-4 mb-4">
            {/* Port Safety Compulsory Floors */}
            <div className="card border-0 shadow-sm mb-4" data-testid="port-safety-floors">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-shield-alt me-2 text-warning" aria-hidden="true" />
                  Port Safety Compulsory Floors
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="perf-port" className="form-label small fw-bold text-muted">
                    Select Target Port / Station
                  </label>
                  <select
                    id="perf-port"
                    className="form-select form-select-sm"
                    value={selectedPort}
                    onChange={(e) => setSelectedPort(e.target.value)}
                    data-testid="port-safety-select"
                  >
                    <option value="">— Select a port / station —</option>
                    {portOptions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  {portOptions.length === 0 && (
                    <small className="text-muted d-block mt-1">
                      No ports are configured on existing allocation rules yet.
                    </small>
                  )}
                </div>

                <div className="mb-3">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="ppa-hard-floor-toggle"
                      checked={enforceHardFloor}
                      onChange={(e) => setEnforceHardFloor(e.target.checked)}
                      data-testid="ppa-hard-floor-toggle"
                    />
                    <label
                      className="form-check-label small fw-bold text-danger"
                      htmlFor="ppa-hard-floor-toggle"
                    >
                      Enforce Hard PPA Minimum Floors
                    </label>
                  </div>
                  <small className="text-muted d-block mt-1">
                    If enabled, bookings below compulsory minimums require a mandatory override
                    rationale or valid thruster deduction flag.
                  </small>
                </div>

                <div className="mb-2">
                  <label htmlFor="thruster-rules" className="form-label small fw-bold text-muted">
                    Thruster Exemption &amp; Deduction Rules
                  </label>
                  <textarea
                    id="thruster-rules"
                    rows={4}
                    className="form-control form-control-sm"
                    value={thrusterRules}
                    onChange={(e) => setThrusterRules(e.target.value)}
                    data-testid="thruster-deduction-rules"
                    placeholder="e.g. Vessels with fully operational bow & stern thrusters exceeding 1,500 HP are eligible for a 1-tug deduction, subject to Port Captain verification."
                  />
                </div>

                <div className="alert alert-info py-2 mb-0 small" data-testid="port-safety-advisory">
                  <i className="fas fa-info-circle me-1" aria-hidden="true" />
                  Enforcement settings are advisory in this build and are not persisted.
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-clipboard-check me-2" aria-hidden="true" />
                  Booking Compliance Check
                </h5>
              </div>
              <div className="card-body">
                <p className="text-muted small">
                  Preview the soft warning a booking would trigger for a vessel GT + proposed tug count.
                </p>
                <form onSubmit={runCheck} noValidate>
                  <div className="mb-3">
                    <label htmlFor="c-gt" className="form-label small fw-bold text-muted">
                      Vessel GT
                    </label>
                    <input
                      id="c-gt"
                      type="number"
                      className={`form-control form-control-sm${checkErrors.gt ? ' is-invalid' : ''}`}
                      value={check.gt}
                      onChange={(e) => {
                        setCheck({ ...check, gt: e.target.value });
                        setCheckErrors((p) => ({ ...p, gt: undefined }));
                      }}
                      data-testid="check-gt"
                    />
                    {checkErrors.gt && (
                      <div className="invalid-feedback" data-testid="check-gt-error">
                        {checkErrors.gt}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="c-tugs" className="form-label small fw-bold text-muted">
                      Proposed Tug Count
                    </label>
                    <input
                      id="c-tugs"
                      type="number"
                      className={`form-control form-control-sm${checkErrors.tugCount ? ' is-invalid' : ''}`}
                      value={check.tugCount}
                      onChange={(e) => {
                        setCheck({ ...check, tugCount: e.target.value });
                        setCheckErrors((p) => ({ ...p, tugCount: undefined }));
                      }}
                      data-testid="check-tugs"
                    />
                    {checkErrors.tugCount && (
                      <div className="invalid-feedback" data-testid="check-tugs-error">
                        {checkErrors.tugCount}
                      </div>
                    )}
                  </div>
                  <button className="btn btn-outline-primary btn-sm w-100" data-testid="run-check">
                    Check Compliance
                  </button>
                </form>

                {checkResult && (
                  <div className="mt-3" data-testid="check-result">
                    {checkResult.error ? (
                      <div className="alert alert-danger py-2 mb-0">{checkResult.error}</div>
                    ) : checkResult.matched === false ? (
                      <div className="alert alert-warning py-2 mb-0" data-testid="check-no-rule">
                        <i className="fas fa-question-circle me-1" aria-hidden="true" />
                        No allocation rule covers GT {checkResult.gt} – needs review.
                      </div>
                    ) : checkResult.belowMinimum ? (
                      <div
                        className={`alert ${checkResult.compulsory ? 'alert-danger' : 'alert-warning'} py-2 mb-0`}
                        data-testid="check-warning"
                      >
                        <i className="fas fa-exclamation-triangle me-1" aria-hidden="true" />
                        {checkResult.warning}
                      </div>
                    ) : (
                      <div className="alert alert-success py-2 mb-0" data-testid="check-ok">
                        <i className="fas fa-check me-1" aria-hidden="true" />
                        Compliant with the allocation matrix.
                      </div>
                    )}
                    {checkResult.overlapCount > 1 && (
                      <div className="small text-muted mt-2" data-testid="check-overlap">
                        {checkResult.overlapCount} overlapping rules – strictest applied (min{' '}
                        {checkResult.minTugCount} tug(s)).
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDel}
        title="Delete allocation rule"
        message={
          confirmDel
            ? `Delete rule GT ${confirmDel.gt_min}–${confirmDel.gt_max ?? '∞'} (min ${confirmDel.min_tug_count} tug(s))?`
            : ''
        }
        busy={deleting}
        onConfirm={() => removeRule(confirmDel)}
        onCancel={() => setConfirmDel(null)}
      />

      {/* Add rule modal */}
      {modalOpen && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="rule-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <form onSubmit={save} noValidate>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      <i className="fas fa-balance-scale me-2 text-primary" aria-hidden="true" />
                      Add Allocation Rule
                    </h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setModalOpen(false)} />
                  </div>
                  <div className="modal-body">
                    {errors.form && <div className="alert alert-danger">{errors.form}</div>}
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label htmlFor="r-gtmin" className="form-label small fw-bold text-muted">
                          GT Minimum <span className="text-danger">*</span>
                        </label>
                        <input
                          id="r-gtmin"
                          type="number"
                          className={`form-control form-control-sm${errors.gtMin ? ' is-invalid' : ''}`}
                          value={form.gtMin}
                          onChange={(e) => setField('gtMin', e.target.value, 'gtMax')}
                          data-testid="gt-min"
                        />
                        {errors.gtMin && <div className="invalid-feedback">{errors.gtMin}</div>}
                      </div>
                      <div className="col-6 mb-3">
                        <label htmlFor="r-gtmax" className="form-label small fw-bold text-muted">
                          GT Maximum
                        </label>
                        <input
                          id="r-gtmax"
                          type="number"
                          className={`form-control form-control-sm${errors.gtMax ? ' is-invalid' : ''}`}
                          value={form.gtMax}
                          onChange={(e) => setField('gtMax', e.target.value)}
                          data-testid="gt-max"
                          placeholder="∞"
                        />
                        {errors.gtMax && <div className="invalid-feedback">{errors.gtMax}</div>}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label htmlFor="r-tugs" className="form-label small fw-bold text-muted">
                          Min Tug Count <span className="text-danger">*</span>
                        </label>
                        <input
                          id="r-tugs"
                          type="number"
                          className={`form-control form-control-sm${errors.minTugCount ? ' is-invalid' : ''}`}
                          value={form.minTugCount}
                          onChange={(e) => setField('minTugCount', e.target.value)}
                          data-testid="min-tugs"
                        />
                        {errors.minTugCount && <div className="invalid-feedback">{errors.minTugCount}</div>}
                      </div>
                      <div className="col-6 mb-3">
                        <label htmlFor="r-bollard" className="form-label small fw-bold text-muted">
                          Min Bollard Pull (T) <span className="text-danger">*</span>
                        </label>
                        <input
                          id="r-bollard"
                          type="number"
                          step="0.1"
                          min="0.1"
                          className={`form-control form-control-sm${errors.minBollardPullTons ? ' is-invalid' : ''}`}
                          value={form.minBollardPullTons}
                          onChange={(e) => setField('minBollardPullTons', e.target.value)}
                          data-testid="min-bollard"
                        />
                        {errors.minBollardPullTons && (
                          <div className="invalid-feedback">{errors.minBollardPullTons}</div>
                        )}
                      </div>
                    </div>
                    <div className="form-check form-switch mb-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="r-compulsory"
                        checked={form.compulsoryFloor}
                        onChange={(e) => setForm({ ...form, compulsoryFloor: e.target.checked })}
                        data-testid="compulsory-floor"
                      />
                      <label className="form-check-label small fw-bold text-muted" htmlFor="r-compulsory">
                        Safety Compulsory Floor (PPA)
                      </label>
                    </div>
                    <div className="mb-1">
                      <label htmlFor="r-exempt" className="form-label small fw-bold text-muted">
                        Exemption Rules
                      </label>
                      <textarea
                        id="r-exempt"
                        rows={2}
                        className="form-control form-control-sm"
                        value={form.exemptionRules}
                        onChange={(e) => setForm({ ...form, exemptionRules: e.target.value })}
                        data-testid="exemption-rules"
                        placeholder="Allowable thruster deductions, etc."
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setModalOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="save-rule">
                      {saving && (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      )}
                      <i className="fas fa-save me-1" aria-hidden="true" />
                      Save Rules Configuration
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
